import type { MandobPayScheme, MandobProfile } from '@erp/shared';
import { ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/shadcn/index.js';
import { Badge, Button, Card, DataTable, EmptyState, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { useAuthStore } from '../../lib/authStore.js';
import { createMandob, listMandobs, updateMandob } from '../../lib/mandob.js';
import { formatDecimals } from '../../lib/money.js';
import { listPriceLists, type PriceList } from '../../lib/pricing.js';


interface CreateForm {
  email: string; fullName: string; password: string;
  salary: string; payScheme: MandobPayScheme; commissionRate: string; territory: string; priceListId: string;
}
const EMPTY: CreateForm = { email: '', fullName: '', password: '', salary: '0', payScheme: 'salary', commissionRate: '0', territory: '', priceListId: '' };

/** Reusable price-list <select> for the create/edit dialogs (hidden when none). */
function PriceListSelect({ id, label, value, lists, onChange }: {
  id: string; label: string; value: string; lists: PriceList[]; onChange: (v: string) => void;
}): JSX.Element | null {
  const active = lists.filter((l) => l.isActive);
  if (active.length === 0) return null;
  return (
    <FormField id={id} label={label}>
      <select id={id} className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {active.map((l) => (
          <option key={l.id} value={l.id}>{l.name}{l.currencyCode ? ` (${l.currencyCode})` : ''}</option>
        ))}
      </select>
    </FormField>
  );
}

export function MandobsPage(): JSX.Element {
  const { t, i18n } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const navigate = useNavigate();
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  // Mandob pay is base-currency money — decimals follow that currency (IQD→0).
  const num = (n: string | number): string => formatDecimals(n, baseCurrency, i18n.language);
  const canManage = usePermission('mandob.manage');
  const [rows, setRows] = useState<MandobProfile[] | null>(null);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<MandobProfile | null>(null);

  const reload = async (): Promise<void> => {
    try { setRows(await listMandobs()); } catch { toast.error(tc('errors:server')); }
  };
  useEffect(() => {
    void reload();
    // Price lists power the per-mandob selector + roster column (empty if the
    // pricing module is off / none defined — the selector then hides itself).
    void listPriceLists().then(setPriceLists).catch(() => setPriceLists([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const priceListName = new Map(priceLists.map((l) => [l.id, l.name] as const));

  if (!rows) {
    return <Card><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;
  }

  const columns = [
    {
      id: 'name', header: t('columns.name', { defaultValue: 'Mandob' }),
      cell: (r: MandobProfile) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-fg">{r.name}</div>
          <div className="truncate text-xs text-fg-muted">{r.email}</div>
        </div>
      ),
      sortValue: (r: MandobProfile) => r.name,
      searchValue: (r: MandobProfile) => `${r.name} ${r.email} ${r.territory ?? ''}`,
    },
    {
      id: 'scheme', header: t('columns.scheme', { defaultValue: 'Pay' }),
      cell: (r: MandobProfile) => (
        <Badge tone={r.payScheme === 'salary_commission' ? 'info' : 'neutral'}>
          {t(`scheme.${r.payScheme}`, { defaultValue: r.payScheme })}
        </Badge>
      ),
    },
    { id: 'salary', header: t('columns.salary', { defaultValue: 'Salary' }), align: 'end' as const, cell: (r: MandobProfile) => <span className="font-mono">{num(r.salary)}</span>, sortValue: (r: MandobProfile) => Number(r.salary) },
    {
      id: 'commission', header: t('columns.commission', { defaultValue: 'Commission' }), align: 'end' as const,
      cell: (r: MandobProfile) => <span className="font-mono text-fg-muted">{r.payScheme === 'salary_commission' ? `${r.commissionRate}%` : '—'}</span>,
    },
    { id: 'territory', header: t('columns.territory', { defaultValue: 'Territory' }), cell: (r: MandobProfile) => r.territory ?? '—', searchValue: (r: MandobProfile) => r.territory ?? '' },
    ...(priceLists.length > 0 ? [{
      id: 'priceList', header: t('columns.priceList', { defaultValue: 'Price list' }),
      cell: (r: MandobProfile) => (r.priceListId ? priceListName.get(r.priceListId) ?? '—' : '—'),
    }] : []),
    {
      id: 'status', header: tc('columns.status', { defaultValue: 'Status' }),
      cell: (r: MandobProfile) => <Badge tone={r.isActive ? 'success' : 'neutral'}>{tc(`status.${r.isActive ? 'active' : 'inactive'}`, { defaultValue: r.isActive ? 'Active' : 'Inactive' })}</Badge>,
    },
    ...(canManage ? [{
      id: 'actions', header: '', align: 'end' as const,
      cell: (r: MandobProfile) => (
        // stopPropagation so the row-level navigate doesn't fire when acting on a button.
        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => setEditing(r)}>{tc('actions.edit')}</Button>
          <ChevronRight className="h-4 w-4 text-fg-subtle rtl:rotate-180" aria-hidden />
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('title', { defaultValue: 'Mandobs' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('subtitle', { defaultValue: 'Your field sales reps — salary, commission, and accounts.' })}</p>
        </div>
        {canManage && <Button onClick={() => setShowCreate(true)}>{t('create.title', { defaultValue: 'Add mandob' })}</Button>}
      </header>

      {rows.length === 0 ? (
        <EmptyState
          title={t('empty', { defaultValue: 'No mandobs yet' })}
          description={t('emptyHint', { defaultValue: 'Add a field sales rep — they get a login and their own workspace to sell and collect.' })}
          action={canManage ? <Button size="sm" onClick={() => setShowCreate(true)}>{t('create.title', { defaultValue: 'Add mandob' })}</Button> : undefined}
        />
      ) : (
        <Card className="p-0">
          <DataTable
            columns={columns}
            data={rows}
            rowKey={(r) => r.id}
            onRowClick={(r) => navigate(`/mandob/${r.id}`)}
            searchPlaceholder={t('search', { defaultValue: 'Search mandobs…' })}
          />
        </Card>
      )}

      {showCreate && <CreateDialog priceLists={priceLists} onClose={() => setShowCreate(false)} onDone={reload} />}
      {editing && <EditDialog mandob={editing} priceLists={priceLists} onClose={() => setEditing(null)} onDone={reload} />}
    </div>
  );
}

function CreateDialog({ priceLists, onClose, onDone }: { priceLists: PriceList[]; onClose: () => void; onDone: () => Promise<void> }): JSX.Element {
  const { t } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const [f, setF] = useState<CreateForm>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<CreateForm>): void => setF((s) => ({ ...s, ...p }));

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await createMandob({
        email: f.email.trim(), fullName: f.fullName.trim(),
        password: f.password.trim() || undefined,
        salary: f.salary || '0', payScheme: f.payScheme,
        commissionRate: Number(f.commissionRate || 0),
        territory: f.territory.trim() || undefined,
        priceListId: f.priceListId || null,
      });
      onClose(); await onDone();
    } catch (err) {
      const code = (err as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      setError(code === 'email_taken' ? t('create.emailTaken', { defaultValue: 'That email is already in use.' }) : tc('errors:server'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <form onSubmit={submit}>
          <DialogHeader><DialogTitle>{t('create.title', { defaultValue: 'Add mandob' })}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 py-3 sm:grid-cols-2">
            <FormField id="md-name" label={t('create.fullName', { defaultValue: 'Full name' })} required>
              <Input id="md-name" value={f.fullName} onChange={(e) => set({ fullName: e.target.value })} required autoFocus />
            </FormField>
            <FormField id="md-email" label={t('create.email', { defaultValue: 'Email (login)' })} required>
              <Input id="md-email" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} required />
            </FormField>
            <FormField id="md-pass" label={t('create.password', { defaultValue: 'Password' })}>
              <Input id="md-pass" type="text" value={f.password} onChange={(e) => set({ password: e.target.value })} placeholder={t('create.passwordHint', { defaultValue: 'min 8 chars' })} />
            </FormField>
            <FormField id="md-territory" label={t('columns.territory', { defaultValue: 'Territory' })}>
              <Input id="md-territory" value={f.territory} onChange={(e) => set({ territory: e.target.value })} />
            </FormField>
            <FormField id="md-salary" label={t('columns.salary', { defaultValue: 'Salary' })}>
              <Input id="md-salary" inputMode="decimal" value={f.salary} onChange={(e) => set({ salary: e.target.value })} />
            </FormField>
            <FormField id="md-scheme" label={t('columns.scheme', { defaultValue: 'Pay scheme' })}>
              <select id="md-scheme" className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg" value={f.payScheme} onChange={(e) => set({ payScheme: e.target.value as MandobPayScheme })}>
                <option value="salary">{t('scheme.salary', { defaultValue: 'Salary only' })}</option>
                <option value="salary_commission">{t('scheme.salary_commission', { defaultValue: 'Salary + commission' })}</option>
              </select>
            </FormField>
            {f.payScheme === 'salary_commission' && (
              <FormField id="md-rate" label={t('create.commissionRate', { defaultValue: 'Commission % (of collections)' })}>
                <Input id="md-rate" type="number" min={0} max={100} step={0.5} value={f.commissionRate} onChange={(e) => set({ commissionRate: e.target.value })} />
              </FormField>
            )}
            <PriceListSelect id="md-pricelist" label={t('columns.priceList', { defaultValue: 'Price list' })} value={f.priceListId} lists={priceLists} onChange={(v) => set({ priceListId: v })} />
          </div>
          {error && <p className="text-sm text-danger" role="alert">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>{tc('actions.cancel')}</Button>
            <Button type="submit" loading={busy} disabled={!f.email.trim() || !f.fullName.trim()}>{t('create.submit', { defaultValue: 'Create mandob' })}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ mandob, priceLists, onClose, onDone }: { mandob: MandobProfile; priceLists: PriceList[]; onClose: () => void; onDone: () => Promise<void> }): JSX.Element {
  const { t } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const [salary, setSalary] = useState(mandob.salary);
  const [payScheme, setPayScheme] = useState<MandobPayScheme>(mandob.payScheme);
  const [commissionRate, setCommissionRate] = useState(String(mandob.commissionRate));
  const [territory, setTerritory] = useState(mandob.territory ?? '');
  const [priceListId, setPriceListId] = useState(mandob.priceListId ?? '');
  const [isActive, setIsActive] = useState(mandob.isActive);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateMandob(mandob.id, {
        salary, payScheme, commissionRate: Number(commissionRate || 0),
        territory: territory.trim() || null, priceListId: priceListId || null, isActive,
      });
      onClose(); await onDone();
    } catch { toast.error(tc('errors:server')); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <form onSubmit={submit}>
          <DialogHeader><DialogTitle>{mandob.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 py-3 sm:grid-cols-2">
            <FormField id="me-salary" label={t('columns.salary', { defaultValue: 'Salary' })}>
              <Input id="me-salary" inputMode="decimal" value={salary} onChange={(e) => setSalary(e.target.value)} />
            </FormField>
            <FormField id="me-territory" label={t('columns.territory', { defaultValue: 'Territory' })}>
              <Input id="me-territory" value={territory} onChange={(e) => setTerritory(e.target.value)} />
            </FormField>
            <FormField id="me-scheme" label={t('columns.scheme', { defaultValue: 'Pay scheme' })}>
              <select id="me-scheme" className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg" value={payScheme} onChange={(e) => setPayScheme(e.target.value as MandobPayScheme)}>
                <option value="salary">{t('scheme.salary', { defaultValue: 'Salary only' })}</option>
                <option value="salary_commission">{t('scheme.salary_commission', { defaultValue: 'Salary + commission' })}</option>
              </select>
            </FormField>
            {payScheme === 'salary_commission' && (
              <FormField id="me-rate" label={t('create.commissionRate', { defaultValue: 'Commission % (of collections)' })}>
                <Input id="me-rate" type="number" min={0} max={100} step={0.5} value={commissionRate} onChange={(e) => setCommissionRate(e.target.value)} />
              </FormField>
            )}
            <PriceListSelect id="me-pricelist" label={t('columns.priceList', { defaultValue: 'Price list' })} value={priceListId} lists={priceLists} onChange={setPriceListId} />
            <label className="flex items-center gap-2 text-sm text-fg sm:col-span-2">
              <input type="checkbox" className="h-4 w-4 accent-brand" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              {tc('status.active', { defaultValue: 'Active' })}
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>{tc('actions.cancel')}</Button>
            <Button type="submit" loading={busy}>{tc('actions.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
